import { memo, useCallback, useContext } from 'react';
import { connect } from 'react-redux';

import { FieldBlock } from 'UIPackage/FormModule/components/FieldBlock';
import { getTitleFieldWithRedStar } from 'functions/getTitleFieldWithRedStar';
import { AgreementsContext } from 'KnmErknmPackage/ErkmAgreementsModule/AgreementsContext';
import { SelectInputField } from 'UIPackage/MainModule/components/SelectInputField';
import { approvalBooleanSelectOptions } from 'KnmPackage/MainModule/constants/approvalBooleanSelectOptions';
import { IErknmFormAgreement } from 'KnmErknmPackage/MainModule/interfaces/IErknmFormAgreement';
import { getKnmFormRef } from 'KnmPackage/MainModule/selectors/knmSelectors';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';
import { IKnmFormRef } from 'KnmPackage/MainModule/interfaces/IKnmFormRef';
import { getDecisionFormRef } from 'DecisionPackage/MainModule/selectors/decisionSelectors';
import { IDecisionFormRef } from 'DecisionPackage/MainModule/interfaces/IDecisionFormRef';
import { IBooleanSelectOption } from 'KnmPackage/MainModule/interfaces/IBooleanSelectOption';
import { IUserOrganisationSelectOption } from 'CorePackage/CatalogsModule/interfaces/IUserOrganisationSelectOption';
import { IDecisionForm } from 'DecisionPackage/MainModule/interfaces/IDecisionForm';
import { IKnmForm } from 'KnmPackage/MainModule/interfaces/IKnmForm';

import styles from './AgreementsApprovalOfAgreement.module.scss';
import { AgreementsApprovalOfAgreementAttachment } from './AgreementsApprovalOfAgreementAttachment';

interface IAgreementsApprovalOfAgreementProps {
  id: string;
  name: string;
  isWatchOnly: boolean;
  agreement: IErknmFormAgreement;
}

interface IAgreementsApprovalOfAgreementStoreProps {
  formRef: IKnmFormRef | undefined;
  decisionFormRef: IDecisionFormRef | undefined;
}

const AgreementsApprovalOfAgreementWithoutStore = memo(
  ({
    id,
    name,
    isWatchOnly,
    agreement,
    formRef,
    decisionFormRef,
  }: IAgreementsApprovalOfAgreementProps & IAgreementsApprovalOfAgreementStoreProps) => {
    const { isKnm, isPm, isDecision } = useContext(AgreementsContext);

    /**
     * Функция очистки формы при изменении значения
     */
    const handleOnChangeApprovalOfAgreement = useCallback(
      (option: IBooleanSelectOption) => {
        if (!option.value) {
          // @ts-ignore
          (isDecision ? decisionFormRef : formRef)?.current.change(`${name}.eliminateViolationsNumber`);
          // @ts-ignore
          (isDecision ? decisionFormRef : formRef)?.current.change(`${name}.conclusionDate`);
          // @ts-ignore
          (isDecision ? decisionFormRef : formRef)?.current.change(`${name}.eliminateViolationsAttachment`);
          // @ts-ignore
          (isDecision ? decisionFormRef : formRef)?.current.change(`${name}.termOfExecutionAgreement`);
          // @ts-ignore
          (isDecision ? decisionFormRef : formRef)?.current.change(`${name}.applicationForConsiderationAttachment`);
          // @ts-ignore
          (isDecision ? decisionFormRef : formRef)?.current.change(`${name}.violationCorrectionProgramAttachments`);
          // @ts-ignore
          (isDecision ? decisionFormRef : formRef)?.current.change(`${name}.otherAttachments`);
          // @ts-ignore
          (isDecision ? decisionFormRef : formRef)?.current.change(`${name}.prosecutorOrganization`);
        } else {
          let prosecutorOrganization: IUserOrganisationSelectOption | undefined;

          if (isDecision) {
            prosecutorOrganization = decisionFormRef?.current.getFieldState(
              'prosecutorOrganization' as keyof IDecisionForm,
            )?.value as IUserOrganisationSelectOption | undefined;
          } else {
            prosecutorOrganization = formRef?.current.getFieldState('prosecutorOrganizationErknm' as keyof IKnmForm)
              ?.value as IUserOrganisationSelectOption | undefined;
          }

          // @ts-ignore
          (isDecision ? decisionFormRef : formRef)?.current.change(
            `${name}.prosecutorOrganization`,
            prosecutorOrganization,
          );
        }
      },
      [formRef, decisionFormRef],
    );

    return (
      <FieldBlock
        label={getTitleFieldWithRedStar({
          label: 'Решение об утверждении ходатайства',
          field: `${name}.approvalOfAgreement`,
          isKnm,
          isPm,
          isDecision,
        })}
        className={styles.Wrapper}
        id={`${id}.approvalOfAgreement`}
      >
        <FieldBlock color="black" className={styles.FieldBlock} margin="bottom">
          <SelectInputField
            name={`${name}.approvalOfAgreement`}
            placeholder={'Решение'}
            size="medium"
            options={approvalBooleanSelectOptions}
            disabled={isWatchOnly}
            onChange={handleOnChangeApprovalOfAgreement}
          />
        </FieldBlock>
        <AgreementsApprovalOfAgreementAttachment
          isWatchOnly={isWatchOnly}
          id={`${id}.approvalOfAgreementAttachment`}
          name={`${name}.approvalOfAgreementAttachment`}
          attachment={agreement?.approvalOfAgreementAttachment}
          formRef={formRef || decisionFormRef}
        />
      </FieldBlock>
    );
  },
);

const mapStateToProps = (state: IRootState): IAgreementsApprovalOfAgreementStoreProps => ({
  formRef: getKnmFormRef(state),
  decisionFormRef: getDecisionFormRef(state),
});

export const AgreementsApprovalOfAgreement = connect(mapStateToProps)(AgreementsApprovalOfAgreementWithoutStore);
